import { useState, useEffect } from "react";
import { Swords, Trophy } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export function AllianceWarsPanel() {
  const [loading, setLoading] = useState(false);
  const [war, setWar] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [showWhenInactive, setShowWhenInactive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  
  useEffect(() => {
    loadWar();
  }, []);
  
  const loadWar = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/alliance-wars/current");
      setWar(res.data.active ? res.data.war : null);
      setVisibility(res.data.visibility || null);
      setShowWhenInactive(Boolean(res.data.visibility?.show_when_inactive));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const startEvent = async () => {
    if (!confirm("Start a new Alliance Wars event? This will notify all eligible alliance leaders.")) return;
    
    setStarting(true);
    try {
      await api.post("/admin/alliance-wars/start", {});
      toast.success("Alliance Wars event started!");
      await loadWar();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setStarting(false);
    }
  };
  
  const forceEnd = async () => {
    if (!confirm("Force-end the current Alliance Wars? This will cancel the event and unlock all fighters.")) return;
    
    setEnding(true);
    try {
      await api.post("/admin/alliance-wars/force-end", {});
      toast.success("Alliance Wars event cancelled");
      await loadWar();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setEnding(false);
    }
  };

  const saveVisibility = async () => {
    setSavingVisibility(true);
    try {
      await api.patch("/admin/alliance-wars/visibility", {
        show_when_inactive: showWhenInactive,
      });
      toast.success("Alliance Wars visibility updated");
      await loadWar();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSavingVisibility(false);
    }
  };

  const visibilityReasonLabel = visibility?.visible_reason === "event_active"
    ? "Visible because an event is running"
    : visibility?.visible_reason === "post_event_grace"
    ? "Visible during the 48-hour results window"
    : visibility?.visible_reason === "manual_override"
    ? "Visible because between-events display is enabled"
    : "Hidden between events";
  
  return (
    <div data-testid="admin-alliance-wars-panel" className="bg-gradient-to-br from-amber-950/10 to-zinc-900/50 border border-amber-900/20 rounded-lg p-6 space-y-5">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Swords className="w-6 h-6 text-amber-500" />
          <div>
            <h3 data-testid="admin-alliance-wars-title" className="text-base font-bold text-amber-400">Alliance Wars</h3>
            <p data-testid="admin-alliance-wars-subtitle" className="text-xs text-zinc-500">10-day competitive event for alliances</p>
          </div>
        </div>
        
        {!loading && (
          <div>
            {!war ? (
              <button
                data-testid="admin-alliance-wars-start-button"
                onClick={startEvent}
                disabled={starting}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-zinc-950 font-bold rounded transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {starting ? "Starting..." : "Start New Event"}
              </button>
            ) : (
              <button
                data-testid="admin-alliance-wars-force-end-button"
                onClick={forceEnd}
                disabled={ending}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ending ? "Ending..." : "Force End Event"}
              </button>
            )}
          </div>
        )}
      </div>
      
      {loading ? (
        <div data-testid="admin-alliance-wars-loading" className="text-sm text-zinc-500">Loading...</div>
      ) : war ? (
        <div className="mt-4 space-y-2 text-sm">
          <div data-testid="admin-alliance-wars-status-row" className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <span className="text-zinc-400">Status:</span>
            <span data-testid="admin-alliance-wars-status-value" className="font-bold text-amber-400 capitalize">{war.status}</span>
          </div>
          <div data-testid="admin-alliance-wars-round-row" className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <span className="text-zinc-400">Current Round:</span>
            <span data-testid="admin-alliance-wars-round-value" className="font-bold text-amber-400">{war.current_round} / 7</span>
          </div>
          <div data-testid="admin-alliance-wars-alliances-row" className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <span className="text-zinc-400">Participating Alliances:</span>
            <span data-testid="admin-alliance-wars-alliances-value" className="font-bold text-amber-400">{war.participating_alliances?.length || 0}</span>
          </div>
          <div data-testid="admin-alliance-wars-submissions-row" className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <span className="text-zinc-400">Submissions:</span>
            <span data-testid="admin-alliance-wars-submissions-value" className="font-bold text-amber-400">
              {Object.keys(war.submissions || {}).length} / {war.participating_alliances?.length || 0}
            </span>
          </div>
        </div>
      ) : (
        <div data-testid="admin-alliance-wars-empty-state" className="mt-4 p-4 bg-zinc-900/30 border border-zinc-800 rounded text-center">
          <Trophy className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p data-testid="admin-alliance-wars-empty-text" className="text-sm text-zinc-500">No active Alliance Wars event</p>
        </div>
      )}

      <div data-testid="admin-alliance-wars-visibility-card" className="border border-amber-900/20 bg-zinc-950/40 rounded-lg p-4 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div data-testid="admin-alliance-wars-visibility-title" className="text-sm font-semibold text-amber-300">Page Visibility</div>
            <p data-testid="admin-alliance-wars-visibility-help" className="text-xs text-zinc-500 mt-1">
              Active events always show. Completed wars stay visible for exactly 48 hours after the event end time. This toggle controls visibility after that grace window.
            </p>
          </div>
          <div data-testid="admin-alliance-wars-visibility-reason" className="text-xs text-amber-300 bg-amber-950/20 border border-amber-900/30 px-3 py-1.5 rounded-full">
            {visibilityReasonLabel}
          </div>
        </div>

        <div data-testid="admin-alliance-wars-visibility-summary" className="grid gap-3 md:grid-cols-3 text-sm">
          <div className="p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <div className="text-zinc-500 text-xs uppercase tracking-wide">Visible Now</div>
            <div data-testid="admin-alliance-wars-visible-now" className="mt-1 font-bold text-amber-400">{visibility?.visible ? "Yes" : "No"}</div>
          </div>
          <div className="p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <div className="text-zinc-500 text-xs uppercase tracking-wide">48h Grace Window</div>
            <div data-testid="admin-alliance-wars-grace-status" className="mt-1 font-bold text-amber-400">{visibility?.within_grace_period ? "Active" : "Inactive"}</div>
          </div>
          <div className="p-3 bg-zinc-900/50 border border-zinc-800 rounded">
            <div className="text-zinc-500 text-xs uppercase tracking-wide">Grace Ends</div>
            <div data-testid="admin-alliance-wars-grace-expires-at" className="mt-1 font-bold text-amber-400 break-words">
              {visibility?.grace_expires_at ? new Date(visibility.grace_expires_at).toLocaleString() : "—"}
            </div>
          </div>
        </div>

        <label data-testid="admin-alliance-wars-show-when-inactive-row" className="flex items-start gap-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded cursor-pointer">
          <input
            data-testid="admin-alliance-wars-show-when-inactive-toggle"
            type="checkbox"
            checked={showWhenInactive}
            onChange={(e) => setShowWhenInactive(e.target.checked)}
            className="mt-1 h-4 w-4 accent-amber-500"
          />
          <div>
            <div data-testid="admin-alliance-wars-show-when-inactive-label" className="text-sm font-medium text-zinc-200">
              Keep Alliance Wars page visible between events
            </div>
            <div data-testid="admin-alliance-wars-show-when-inactive-note" className="text-xs text-zinc-500 mt-1">
              Turn this off to fully hide the page once the 48-hour post-event review window ends.
            </div>
          </div>
        </label>

        <div className="flex justify-end">
          <button
            data-testid="admin-alliance-wars-save-visibility-button"
            onClick={saveVisibility}
            disabled={savingVisibility || loading}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-zinc-950 font-bold rounded transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingVisibility ? "Saving..." : "Save Visibility"}
          </button>
        </div>
      </div>
    </div>
  );
}
